import {
  Typography,
  Card,
  Row,
  Col,
  Button,
  Switch,
  Spin,
  Alert,
  Tooltip,
  Input,
  Menu,
  Tag,
  Modal,
  Select,
  Popconfirm,
  Divider,
  Result,
  Form,
  Space,
  message,
} from "antd";

import { Link } from "react-router-dom";
import { useEffect, useState, useMemo, useDebugValue } from "react";
import { getIcon } from "./atoms/FuzzyIcon";
import { useDispatch, useSelector } from "react-redux";
import {
  DeleteOutlined,
  SettingOutlined,
  SmileOutlined,
  ReloadOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import apiAction from "../ajax/service";
import TableView from "./atoms/TableView";
import {
  addNewType,
  cancelChange,
  saveChangesLocally,
  setChanges,
  setToBeSaved,
} from "../features/workspaceSlice";
import { store } from "./../app/store";
import { firstLetterCaps, getRandomColor, uuid6 } from "../helpers/helper";
import {
  addMemberToWorkspace,
  deleteWorkspaceMember,
  generateApiKey,
  getWorkspaceMembers,
  manageWebhooks,
  manageWorkspaceRole,
  setWorkspaceConfig,
  setWorkspaceMemberRole,
} from "../interfaces/workspaceSettingsInterface";
import LabeledBox from "./atoms/LabeledBox";
import { isEqual } from "lodash";
import TaxConfigManager from "./atoms/rentlog/TaxConfigManager";
import WebhookManager from "./atoms/rentlog/WebhookManager";
import TemplatePreview from "./invoicebuilder/components/PreviewTemplate";

const { Title, Text } = Typography;

function RoleForm({
  existingRoles = [],
  users = [],
  roleKey,
  action,
  value,
  onChange,
  onSubmit,
  onCancel,
  onDelete,
  setUserRoleForm, // new optional prop
  saving = false,
}) {
  const defaultValues = {
    name: "",
    forms: {
      allowFormPosting: false,
    },
    sensitiveInfoBlock: {
      amount: false,
      tenantInfo: false,
      analytics: false,
    },
  };
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const [alert, setAlert] = useState("");
  const [canDelete, setCanDelete] = useState(action.startsWith("edit"));
  const [disableSave , setDisableSave] = useState(action.startsWith("edit")?false:true)
  const [formValues,setFormValues]=useState(defaultValues)


  // sync incoming values
  useEffect(() => {

    if (action == "edit" && roleKey) {
      for (var role of existingRoles) {
        if (role.name == roleKey) {
          value = JSON.parse(role.config);
          value["name"] = role.name;
        }
      }
    }

    for (var user of users) {
      if (action.startsWith("edit")) {
        if (user.role === roleKey) {
          setCanDelete(false);
          setAlert(
            "Cannot be Deleted , This role is already assigned to users"
          );
        }
      }
    }
    setFormValues(value)
    form.setFieldsValue(value);
  }, [value, form]);

  // watch allowFormPosting to auto-clear dependents
  const allowFormPosting = Form.useWatch(["forms", "allowFormPosting"], form);

  useEffect(() => {
    if (allowFormPosting === false) {
      const current = form.getFieldsValue(true);
      const updated = {
        ...current,
        forms: {
          ...current.forms,
          allowSensitiveInFormPosting: false,
          allowEditSensitiveInFormPosting: false,
        },
      };
      form.setFieldsValue(updated);
      onChange && onChange(updated);
    }
  }, [allowFormPosting]);

  // helper to filter name input to letters only (A-Z, a-z)
  const handleNameChange = (e) => {

    const raw = e.target.value || "";
    const filtered = raw.replace(/[^A-Za-z]/g, "");
    let shouldDisable = false
    existingRoles.map((item)=>{
      if(item.name == filtered){
        message.error('Role name already exist')
        shouldDisable = true
      }
    })
    if(shouldDisable !== disableSave){
      setDisableSave(shouldDisable)
    }
    form.setFieldsValue({ name: filtered });
    onChange && onChange({ ...form.getFieldsValue(true), name: filtered });
  };

  const SwitchRow = ({
    label,
    namePath,
    tooltip,
    disabled = false,
    bold = false,
  }) => (
    <Form.Item shouldUpdate noStyle>
      {() => (
        <Row
          align="middle"
          justify="space-between"
          style={{ width: "100%", padding: "8px 0" }}
        >
          <Col flex="1 1 0" style={{ paddingRight: 12 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <Text style={bold ? { fontWeight: 600 } : {}}>{label}</Text>
              {tooltip && (
                <Tooltip title={tooltip}>
                  <InfoCircleOutlined style={{ color: "rgba(0,0,0,0.45)" }} />
                </Tooltip>
              )}
            </div>
          </Col>
          <Col>
            <Form.Item name={namePath} valuePropName="checked" noStyle>
              <Switch disabled={disabled} />
            </Form.Item>
          </Col>
        </Row>
      )}
    </Form.Item>
  );

  const handleFinish = (vals) => {
    const name = vals.name;
    const normalized = {
      forms: {
        allowFormPosting: !!vals.forms?.allowFormPosting,
        allowSensitiveInFormPosting: !!vals.forms?.allowSensitiveInFormPosting,
        allowEditSensitiveInFormPosting:
          !!vals.forms?.allowEditSensitiveInFormPosting,
      },
      sensitiveInfoBlock: {
        amount: !!vals.sensitiveInfoBlock?.amount,
        tenantInfo: !!vals.sensitiveInfoBlock?.tenantInfo,
        analytics: !!vals.sensitiveInfoBlock?.analytics,
      },
    };

    for (var role of existingRoles) {
      if (action.startsWith("add")) {
        if (role.name === name) {
          setAlert("User Role already exists!!!");
        }
        if (isEqual(JSON.parse(role.config), normalized)) {
          setAlert(
            `Same User Role Configuration Exist as ${role.name} . Please verify .`
          );
        }
      }
    }
    onSubmit && onSubmit(name, normalized);
  };

  return (
    <Card bordered={false} style={{ maxWidth: 900 }}>
      {alert.length > 0 && (
        <Alert closable className="mb-2" message={alert} icon />
      )}
      <Typography.Title level={5}>
        User Role Form <Tag color="red">Verify Before Assigning</Tag>
        <span
          className="float-right cursor-pointer"
          onClick={() => setUserRoleForm("")}
        >
          X
        </span>
      </Typography.Title>

      <Form
        form={form}
        layout="vertical"
        initialValues={formValues}
        onValuesChange={(_, all) => onChange && onChange(all)}
        onFinish={handleFinish}
      >
        <Row gutter={16}>
          {/* LEFT: name on top, forms permissions directly below */}
          <Col xs={24} sm={14}>
            <Form.Item
              label={<span style={{ fontWeight: 600 }}>Role name</span>}
              name="name"
              rules={[
                { required: true, message: "Role name is required" },
                {
                  pattern: /^[A-Za-z]+$/,
                  message: "Only letters allowed (A–Z). No spaces or symbols.",
                },
                { min: 3, message: "At least 3 characters" },
              ]}
            >
              <Input
                placeholder="e.g. BillingAnalyst"
                onChange={handleNameChange}
              />
            </Form.Item>

            <Card
              type="inner"
              title="Form Permissions"
              size="small"
              style={{ marginBottom: 12 }}
            >
              <Space direction="vertical" style={{ width: "100%" }}>
                <SwitchRow
                  label="Allow Form Posting"
                  namePath={["forms", "allowFormPosting"]}
                  tooltip="Users can submit forms"
                  bold
                />


              </Space>
            </Card>
          </Col>

          {/* RIGHT: delete controls on top, sensitive info block pushed down */}
          <Col xs={24} sm={10}>
            {/* Delete button + Review Danger tag (pushes sensitive card down) */}
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                marginBottom: 12,
              }}
            >
              <Space size="middle" align="center">
                {canDelete && (
                  <Popconfirm
                    title="Are you sure you want to delete this role?"
                    onConfirm={() => onDelete && onDelete()}
                    okText="Delete"
                    cancelText="Cancel"
                  >
                    <Button danger disabled={saving}>
                      Delete
                    </Button>
                  </Popconfirm>
                )}
              </Space>
            </div>

            <Card type="inner" title="Allow Sensitive Info" size="small">
              <Space direction="vertical" style={{ width: "100%" }}>
                <SwitchRow
                  label="Financials"
                  namePath={["sensitiveInfoBlock", "amount"]}
                  tooltip="Allows access to financial-related fields"
                />
                <SwitchRow
                  label="Customer Info"
                  namePath={["sensitiveInfoBlock", "tenantInfo"]}
                  tooltip="Allows access to tenant PII"
                />
                <SwitchRow
                  label="Analytics"
                  namePath={["sensitiveInfoBlock", "analytics"]}
                  tooltip="Allows access to analytics-related data"
                />
              </Space>
            </Card>
          </Col>
        </Row>

        <Divider />

        <Row justify="end" gutter={12}>
          {onCancel && (
            <Col>
              <Button
                onClick={() => {
                  onCancel();
                  form.resetFields();
                }}
              >
                Cancel
              </Button>
            </Col>
          )}
          <Col>
            <Button type="primary" htmlType="submit" loading={saving} disabled={disableSave}>
              Save
            </Button>
          </Col>
        </Row>
      </Form>
    </Card>
  );
}

const WorkspaceSettingsView = () => {
  const [mainTab, setMainTab] = useState("workspace");
  const [subTab, setSubTab] = useState("types");
  const [openKeys, setOpenKeys] = useState([]);
  const dispatch = useDispatch();
  const types = useSelector((state) => state.workspace.types || {});
  const taxConfigs = useSelector((state) => state.workspace.taxConfigs || []);
  const typeKeys = useMemo(() => Object.keys(types || {}), [types]);
  const hasLocalChanges = useSelector(
    (state) => state.workspace.hasLocalChanges
  );
  const apiKey = useSelector((state) => state.workspace.apiKey);
  const enableApiKey = useSelector((state) => state.workspace.enableApiKey);
  const workspaceId = useSelector((state) => state.workspace.id ? state.workspace.id: sessionStorage.getItem('selectedWorkspace'));
  const [typesAlert, setTypesAlert] = useState(false);
  const [typesCount, setTypesCount] = useState({});
  const constants = useSelector((state) => state.workspace.constants || {});
  const conditionals = useSelector(
    (state) => state.workspace.conditionals || {}
  );
  const [addEmail, setAddEmail] = useState("");
  const [addRole, setAddRole] = useState("");
  const [emailModal, setEmailModal] = useState(false);
  const [workspaceMemberId, setWorkspaceMemberId] = useState("");
  const loggedInUser = useSelector(state=>state.workspace.loggedInUser)
  const edits = useSelector((state) => state.workspace.changes || {});
  const [loadingCounts, setLoadingCounts] = useState(false);
  const [countErr, setCountErr] = useState("");
  const [forceEditableKey, setForceEditableKey] = useState("");
  const workspaceSettingsId = useSelector(
    (state) => state.workspace.workspaceSettingsId
  );
  const workspaceMembers = useSelector((state) => state.workspace.members);
  const roles = useSelector((state) => state.workspace?.roles);
  const [userRoleForm, setUserRoleForm] = useState("");
  const descriptions = useSelector(
    (state) => state.workspace?.preload?.constants?.descriptions
  );
  const [disableUserSave,setDisableUserSave]= useState(true)

    useEffect(()=>{
    if(!addRole && roles.length>0){
      setAddRole(roles?.[0]?.name)
    }
  },[roles])
  // should be cleaned up

  useEffect(() => {
    let cancelled = false;
    const fetchCounts = async () => {
      if (!typeKeys.length) {
        setTypesCount({});
        return;
      }
      try {
        setLoadingCounts(true);
        setCountErr("");
        const data = await apiAction(
          "stats",
          { model: "rental", field: "type", values: typeKeys },
          "countByValues"
        );
        if (!cancelled) setTypesCount(data?.data?.byValue || {});
      } catch (e) {
        if (!cancelled) setCountErr(e?.message || "Failed to load counts");
      } finally {
        if (!cancelled) setLoadingCounts(false);
      }
    };
    fetchCounts();
    getWorkspaceMembers(dispatch, workspaceId);
    manageWorkspaceRole(dispatch, "", {}, "get_workspace_role");

    return () => {
      cancelled = true;
    };
  }, [typeKeys]);

  // Menu items (clean labels)
  const menuItems = useMemo(
    () => [
      {
        key: "workspace",
        icon: getIcon("workspace settings", true, {
          size: 30,
          title: "Workspace Settings",
        }),

        children: [
          { key: "workspace:types", label: "Types" },
          {key: "workspace:taxconfigs", label: "Tax Configuration" },
          { key: "workspace:constants", label: "Constants and Default Currency" },
          { key: "workspace:conditionals", label: "Conditionals" },

        ],
      },
      {
        key: "user",
        icon: getIcon("user settings", true, {
          size: 30,
          title: "User Settings",
        }),

        children: [{ key: "user:profile", label: "Users & Access" }],
      },
      {
        key: "autoposting",
        icon: getIcon("Auto Posting", true, {
          size: 30,
          title: "Auto Posting",
        }),

        children: [
          { key: "autoposting:apikey", label: "API Key for AutoPosting" },
          { key: "autoposting:webhooks", label: "Webhooks for App Events" },
        ],
      },
    ],
    []
  );

  // Breadcrumb heading (Workspace Settings / Types)
  const mainLabelMap = {
    workspace: "Workspace Settings",
    user: "User Settings",
    autoposting: "Auto Posting",
  };

  const subLabelMap = {
    types: "Types",
    constants: "Constants",
    conditionals: "Conditionals",
    profile: "Profile",
    apikey: "API Key",
    "taxconfigs": "Tax Configurations",
    webhooks:'Manage Webhooks'
  };

  const headingBreadcrumb = `${mainLabelMap[mainTab]} / ${
    subLabelMap[subTab] || ""
  }`;

  // Hover-collapse behavior
  const onOpenChange = (keys) => {
    setOpenKeys(keys.slice(-1)); // keep only last opened
  };

  const onMenuClick = ({ key }) => {
    const [root, child] = key.split(":");
    setMainTab(root);
    setSubTab(child);
  };

  const selectedKeys = [`${mainTab}:${subTab}`];

  return (
    <div>
      <Title level={4}>
        {getIcon("workspace", true, { size: 40 })} Manage Your Workspace
      </Title>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "20% 1fr", // 25% / 75%
          gap: 16,
          alignItems: "flex-start",
        }}
      >
        {/* LEFT SIDEBAR */}
        <div
          // onMouseLeave={() => setOpenKeys([])}
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 12,
            height: "100%",
          }}
        >
          <Card size="small" style={{ borderRadius: 12 }}>
            <Title level={5} style={{ margin: 0 }}>
              Manage
            </Title>
            <Text type="secondary">Workspace & User sections</Text>
          </Card>

          <Menu
            mode="inline"
            items={menuItems}
            openKeys={openKeys}
            onOpenChange={onOpenChange}
            selectedKeys={selectedKeys}
            onClick={onMenuClick}
            triggerSubMenuAction="hover"
            style={{ borderRight: 0 }}

          />
        </div>

        {/* RIGHT CONTENT */}
        <div style={{ minHeight: 240 }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 12,
            }}
          >
            <Title level={5} style={{ margin: 0 }}>
              {headingBreadcrumb}
            </Title>
            {hasLocalChanges && (
              <Alert
                showIcon
                type="warning"
                action={
                  <Button
                    className="ml-2"
                    size="small"
                    type="primary"
                    onClick={() =>
                      setWorkspaceConfig(dispatch, workspaceSettingsId, {
                        types: types,
                        constants: constants,
                        conditionals: conditionals,
                        taxConfigs: taxConfigs
                      })
                    }
                  >
                    Save
                  </Button>
                }
                message="You have unsaved local Changes , Save to avoid changes getting lost. "
              />
            )}
            {/* {mainTab === "workspace" && subTab === "types" && (
              <Button type="primary">Add</Button>
            )} */}
          </div>

          {countErr ? (
            <Alert
              type="error"
              message={countErr}
              showIcon
              style={{ marginBottom: 12 }}
            />
          ) : null}

          {mainTab === "workspace" && subTab === "types" && (
            <Spin spinning={loadingCounts}>
              <Card>
                <Row>
                  <Col span={22}>
                    <Text type="secondary">
                      Define key/value constants, units, tax rates, etc.
                    </Text>
                  </Col>
                  <Col span={2}>
                    <Button
                    className="float-right"
                      size="small"
                      type="primary"
                      onClick={() => {
                        let uuid = uuid6();
                        dispatch(addNewType(uuid));
                        setTypesCount((typesCount) => {
                          return { ...typesCount, uuid: 0 };
                        });
                        setForceEditableKey(uuid);
                      }}
                    >
                      Add
                    </Button>
                  </Col>
                </Row>
                {typesAlert && (
                  <Alert
                    message={"Types Name cannot be empty or less than 3 chars "}
                    showIcon
                  />
                )}
                <TableView
                  dataKey={"key"}
                  canDeleteKeys={
                    new Set(
                      Object.keys(typesCount).filter(
                        (item) => typesCount[item] == 0
                      )
                    )
                  }
                  onDelete={async (key) => {
                    let data = await apiAction(
                      "stats",
                      { model: "rental", field: "type", values: typeKeys },
                      "countByValues"
                    );
                    data = data?.data?.byValue;
                    if (key in data && data[key] == 0) {
                      const updatedTypes = { ...types };
                      delete updatedTypes[key];
                      setWorkspaceConfig(dispatch, workspaceSettingsId, {
                        types: updatedTypes,
                        constants: constants,
                        conditionals: conditionals,
                        taxConfigs:taxConfigs
                      });
                    }
                    setTypesCount(data || {});
                  }}
                  forceEditableKey={forceEditableKey}
                  title={"Manage Types"}
                  hasDelete={true}
                  hasEditableRow={true}
                  data={Object.keys(types).map((item) => {
                    return {
                      key: item,
                      UUID: item,
                      taxConfig: types[item]["taxConfig"],
                      name: types[item]["name"],
                      productsCount: typesCount[item] ?? 0,
                    };
                  })}
                  onEditStart={(key) => {
                    dispatch(
                      setChanges({
                        changeKey: "types",
                        key,
                        value: types[key],
                      })
                    );
                  }}
                  onEditCancel={(key) =>
                    dispatch(cancelChange({ changeKey: "types", key }))
                  }
                  onEditComplete={(key) => {
                    if (edits["types"][key]) {
                      if (edits["types"][key]["name"].length < 3) {
                        setTypesAlert(true);
                        return;
                      }
                    }
                    dispatch(saveChangesLocally({ changeKey: "types", key }));
                    if (typesAlert) setTypesAlert(false);
                  }}
                  showHideColumns={["UUID"]}
                  columns={[
                    {
                      key: "UUID",
                      title: "UUID",
                      dataIndex: "UUID",
                    },
                    {
                      key: "Name",
                      title: "Name",
                      dataIndex: "name",
                      editable: true,
                      render: (_, record) => {
                        if (edits?.types[record.key]) {
                          return (
                            <Input
                              required
                              value={edits["types"][record.key]["name"]}
                              onChange={(e) =>
                                dispatch(
                                  setChanges({
                                    changeKey: "types",
                                    keyPath: [record.key, "name"],
                                    value: e.target.value,
                                  })
                                )
                              }
                            />
                          );
                        } else {
                          return record["name"];
                        }
                      },
                    },
         {
                      key: "taxConfig",
                      title: "Tax Config",
                      dataIndex: "taxConfig",
                      editable: true,
                      render: (_, record) => {
                        if (edits?.types[record.key]) {
                          return (
                            <Select
                            style={{width:'80%'}}
                            onChange={(value)=>dispatch(
                                  setChanges({
                                    changeKey: "types",
                                    keyPath: [record.key, "taxConfig"],
                                    value: value,
                                  })
                                )}
                              value={edits?.types[record.key]['taxConfig']}
                              options={taxConfigs.map((config)=>{return {label:config.name,key:config.id,value:config.id}})} />
                          );
                        } else {
                          return (
                            <>{taxConfigs.filter((config)=>config.id == types[record.key]['taxConfig'])?.[0]?.name}</>
                            // <Select style={{width:'80%'}} onChange={(value)=>dispatch(
                            //       setChanges({
                            //         changeKey: "types",
                            //         keyPath: [record.key, "taxConfig"],
                            //         value: value,
                            //       })
                            //     )} options={taxConfigs.map((config)=>{return {label:config.name,key:config.id,value:config.id}})} />
                          );
                        }
                      },
                    },

                    {
                      key: "productsCount",
                      title: "Products Count",
                      dataIndex: "productsCount",
                    },
                  ]}
                />
                {/* <ManageTypes types={types} typesCount={typesCount} /> */}
              </Card>
            </Spin>
          )}
          {mainTab === "workspace" && subTab === "taxconfigs" && (
            <TaxConfigManager
            types={types}
            taxConfigs={taxConfigs}
            onSave={
              (value)=>
                {
                  dispatch(setChanges({changeKey:'taxConfigs',key:'',value}))
                  dispatch(saveChangesLocally({changeKey:'taxConfigs',key:''}))
                }
              }/>
          )
          }
          {mainTab === "workspace" && subTab === "constants" && (
            <Card>
              <Text type="secondary">
                Define key/value constants, units, tax rates, etc.
              </Text>
              <TableView
                title={"Manage Constants"}
                dataKey={"key"}
                data={Object.keys(constants).map((item) => {
                  return {
                    key: item,
                    description: descriptions[item],
                    value: constants[item],
                  };
                })}
                onEditStart={(key) => {
                  dispatch(
                    setChanges({
                      changeKey: "constants",
                      key,
                      value: constants[key],
                    })
                  );
                }}
                onEditCancel={(key) =>
                  dispatch(cancelChange({ changeKey: "constants", key }))
                }
                onEditComplete={(key) => {

                  dispatch(saveChangesLocally({ changeKey: "constants", key }));
                }}
                showHideColumns={["CONST_KEY"]}
                hasEditableRow={true}
                columns={[
                  { key: "CONST_KEY", title: "CONST_KEY", dataIndex: "key" },
                  {
                    key: "Description",
                    title: "Description",
                    dataIndex: "description",
                    width: "65%",
                  },
                  {
                    title: "Value",
                    dataIndex: "value",
                    editable: true,

                    render: (_, record) => {
                      if (record.key in edits?.constants) {
                        return (
                          <Input
                            required
                            value={edits["constants"][record.key]}
                            onChange={(e) =>
                              dispatch(
                                setChanges({
                                  changeKey: "constants",
                                  key: record.key,
                                  value: e.target.value,
                                })
                              )
                            }
                          />
                        );
                      } else {
                        return record["value"];
                      }
                    },
                  },
                ]}
              />
            </Card>
          )}

          {mainTab === "workspace" && subTab === "conditionals" && (
            <Card>
              <Text type="secondary">
                Rules for pricing, availability, and validations.
              </Text>
              <TableView
                title={"Manage Conditionals"}
                dataKey={"key"}
                data={Object.keys(conditionals).map((item) => {
                  return {
                    key: item,
                    description: descriptions[item],
                    value: conditionals[item],
                  };
                })}
                onEditStart={(key) => {
                  dispatch(
                    setChanges({
                      changeKey: "conditionals",
                      key,
                      value: conditionals[key],
                    })
                  );
                }}
                onEditCancel={(key) =>
                  dispatch(cancelChange({ changeKey: "conditionals", key }))
                }
                onEditComplete={(key) => {
                  dispatch(
                    saveChangesLocally({ changeKey: "conditionals", key })
                  );
                }}
                showHideColumns={["CONDITIONAL_KEY"]}
                hasEditableRow={true}
                columns={[
                  {
                    key: "CONDITIONAL_KEY",
                    title: "CONDITIONAL_KEY",
                    dataIndex: "key",
                  },
                  {
                    key: "Description",
                    title: "Description",
                    dataIndex: "description",
                    width: "65%",
                  },
                  {
                    title: "Value",
                    dataIndex: "value",
                    editable: true,

                    render: (_, record) => {
                      if (record.key in edits?.conditionals) {
                        return (
                          <Switch
                            className="m-1"
                            checked={edits?.conditionals[record.key]}
                            checkedChildren="On"
                            unCheckedChildren="Off"
                            onChange={(e) =>
                              dispatch(
                                setChanges({
                                  changeKey: "conditionals",
                                  key: record.key,
                                  value: e,
                                })
                              )
                            }
                          />
                        );
                      } else {
                        return (
                          <Switch
                            className="m-1"
                            disabled
                            checked={conditionals[record.key]}
                            checkedChildren="On"
                            unCheckedChildren="Off"
                          />
                        );
                      }
                    },
                  },
                ]}
              />
            </Card>
          )}

          {mainTab === "user" && subTab === "profile" && (
            <Card>
              <Modal
                // okButtonProps = {{disabled:disableUserSave}}
                style={{ minHeight: 400 }}
                width={"50%"}
                title={emailModal == "edit" ? "Edit User" : "Create User"}
                open={emailModal.length > 0}
                onOk={() => {
                  emailModal == "add"
                    ? addMemberToWorkspace(
                        dispatch,
                        workspaceId,
                        addEmail,
                        addRole
                      )
                    : setWorkspaceMemberRole(
                        dispatch,
                        workspaceId,
                        addEmail,
                        addRole
                      );
                  setAddEmail("");
                  setEmailModal("");
                }}
                onCancel={() => {
                  setAddEmail("");
                  setEmailModal("");
                  setUserRoleForm("");
                }}
                okText={emailModal == "edit" ? "Update User" : "Create User"}
                okButtonProps={{ disabled: userRoleForm || disableUserSave }}
              >
                <Input
                  className="mb-2"
                  value={addEmail}
                  placeholder="Enter email to add user ."
                  type="email"
                  onChange={(e) =>{
                    let shouldDisableSave = false
                    workspaceMembers.map((item)=>{
                      if (item?.user?.email === e.target.value){
                        shouldDisableSave = true
                      }
                    })
                    if(e.target.value == loggedInUser){
                      shouldDisableSave = true
                    }
                    if (shouldDisableSave != disableUserSave){
                      setDisableUserSave(shouldDisableSave)

                    }
                    setAddEmail(e.target.value)}}
                  required={true}
                  disabled={emailModal == "edit"}
                />
                <Row className="mt-3 mb-6">
                  <Col span={12}>
                    <LabeledBox
                      color={getRandomColor(true)}
                      label={"Select User Role"}
                      style={{ width: "100%", padding: 5 }}
                    >
                      <Select
                        className="w-[100%] "
                        options={
                          roles &&
                          roles.map((role) => {
                            return {
                              label: role.name,
                              value: role.name,
                            };
                          })
                        }

                        value={addRole }
                        onChange={(e) => {
                          setAddRole(e);
                          setUserRoleForm("");
                        }}
                      />
                    </LabeledBox>
                  </Col>
                  <Col span={10} className=" ml-2">
                    <LabeledBox
                      label={"User Role Setting"}
                      color={getRandomColor(true)}
                      style={{ padding: 5, float: "right", width: "100%" }}
                    >
                      {addRole.length > 0 && (
                        <Button
                          type="primary"
                          className="w-[60%] "
                          onClick={() => setUserRoleForm("edit")}
                        >
                          Modify Selected
                        </Button>
                      )}
                      <Button
                        type="primary"
                        className={
                          addRole.length > 0 ? "w-[35%] ml-1" : "w-[100%]"
                        }
                        onClick={() => setUserRoleForm("add")}
                      >
                        Add New{" "}
                      </Button>
                    </LabeledBox>
                  </Col>
                </Row>
                {userRoleForm.length > 0 && (
                  <>
                    <Divider />
                    <RoleForm
                      onDelete={() => {
                        manageWorkspaceRole(
                          dispatch,
                          addRole,
                          {},
                          "delete_workspace_role"
                        );
                        setUserRoleForm("");
                        setAddRole("");
                      }}
                      users={workspaceMembers}
                      setUserRoleForm={() => setUserRoleForm("")}
                      existingRoles={roles}
                      roleKey={addRole}
                      action={userRoleForm}
                      onSubmit={(name, values) => {
                        manageWorkspaceRole(
                          dispatch,
                          name,
                          JSON.stringify(values),
                          userRoleForm == "add"
                            ? "add_workspace_role"
                            : "update_workspace_role"
                        );
                        setUserRoleForm("");
                        message.success("Role Modification Successful");
                      }}
                    />
                  </>
                )}
              </Modal>

              <Text type="secondary">
                View and edit user information, contact details, etc.{" "}
                <Button
                  size="small"
                  className="float-right"
                  type="primary"
                  onClick={() => setEmailModal("add")}
                >
                  Add User
                </Button>
              </Text>
              <TableView
                title={"Manage Users & Permissions"}
                data={workspaceMembers.map((member) => {
                  return {
                    email: member.user.email,
                    id: member.user.id,
                    role: member.role,
                  };
                })}
                showHideColumns={["id"]}
                columns={[
                  {
                    key: "id",
                    title: "Id",
                    dataIndex: "id",
                  },
                  {
                    key: "email",
                    title: "Email",
                    dataIndex: "email",
                  },
                  {
                    key: "role",
                    title: "Role",
                    dataIndex: "role",
                    render: (_, record) => {
                      if (record.role === "admin") {
                        return <Tag color="error">Admin </Tag>;
                      } else {
                        return <Tag color="blue">{record.role}</Tag>;
                      }
                    },
                  },
                  {
                    key: "action",
                    title: "Action",
                    width: "20%",
                    render: (_, record) => {
                      return (
                        <>
                          <a
                            className="text-blue-700"
                            onClick={() => {
                              setEmailModal("edit");
                              setAddEmail(record.email);
                              setAddRole(record.role);
                              setWorkspaceMemberId(record.memberId);
                            }}
                          >
                            Edit
                          </a>
                          <Popconfirm
                            okText="Delete"
                            onConfirm={() =>
                              deleteWorkspaceMember(
                                dispatch,
                                workspaceId,
                                record.email
                              )
                            }
                            description={"Sure you want to delete ?"}
                          >
                            <Divider type="vertical" />
                            <a className="text-blue-700">Delete</a>
                          </Popconfirm>
                        </>
                      );
                    },
                  },
                ]}
              />
            </Card>
          )}

          {mainTab === "autoposting" && subTab === "apikey" && (
            <Card
              title={"Auto Posting"}
              extra={
                <>
                  <Switch

                    className="ml-2"
                    unCheckedChildren="Disabled"
                    checkedChildren="Enabled"
                    checked={enableApiKey}
                    onChange={async(e) =>
                     await generateApiKey(dispatch, workspaceSettingsId, e)
                    }
                  />
                </>
              }
            >
              <Text type="secondary">API Key for Auto Posting</Text>
              {apiKey == "" ? (
                <Result
                  icon={<SmileOutlined />}
                  title=" Generate API Key to get Started!"
                  extra={
                    <Button
                      type="primary"
                      onClick={() =>
                        generateApiKey(dispatch, workspaceSettingsId, true)
                      }
                    >
                      Generate
                    </Button>
                  }
                />
              ) : (
                <>
                  <Row className="p-4">
                    <Col span={8}>
                      <LabeledBox color={"red"} label={"WorkspaceId"}>
                        <Text
                          id="workspaceId"
                          aria-value={workspaceId}
                          ellipsis
                          disabled={enableApiKey == false}
                          copyable
                          className="text-[15px]"
                        >
                          {workspaceId}
                        </Text>
                      </LabeledBox>
                    </Col>
                    <Col span={16}>
                      <LabeledBox
                        color={"green"}
                        label={"API Key"}
                        extras={
                          <Tag
                            style={{ cursor: "pointer" }}
                            className="ml-1"
                            onClick={() =>
                              generateApiKey(
                                dispatch,
                                workspaceSettingsId,
                                true
                              )
                            }
                          >
                            <ReloadOutlined /> Refresh
                          </Tag>
                        }
                      >
                        <Text
                        id="apikey"
                          ellipsis
                          value={apiKey}
                          disabled={enableApiKey == false}
                          copyable
                          className="text-[15px]"
                        >
                          {apiKey}
                        </Text>
                      </LabeledBox>
                    </Col>
                  </Row>
                </>
              )}
              {apiKey !== ""  && <div style={{height:'600px'}}>
              <TemplatePreview url="/api/template/apicontract" inIFrame={true}/>
              </div>}
            </Card>
          )}
          {
            mainTab === "autoposting" && subTab === "webhooks" && (
              <WebhookManager
              testWebhook={async (data)=>{
                await manageWebhooks(dispatch,data,'test_webhook')
                message.success("Message Sent")
              }}
              onSave={(data , action)=>{
                  manageWebhooks(dispatch,data,action)
              }} />
            )
          }

          {/* {mainTab === "user" && subTab === "preferences" && (
            <Card>
              <Title level={5}>User Preferences</Title>
              <Text type="secondary">
                Configure theme, notifications, and UI layout settings.
              </Text>
            </Card>
          )} */}
        </div>
      </div>
    </div>
  );
};

export default WorkspaceSettingsView;
